// src/admin/context/AdminDataContext.jsx
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  initialDailyCA,
  initialMonthlyCA,
  initialPrelimsTests,
  initialMainsInitiatives,
  initialStudentResults,
  initialActivities,
  initialSettings,
} from "../data/initialAdminData";
import {
  initialPrelimsQuestionBank,
  initialMainsQuestionBank,
} from "../data/initialQuestionBankData";
import {
  dailyCAAPI,
  monthlyCAAPI,
  prelimsAPI,
  mainsAPI,
  qbAPI,
  resultsAPI,
  dashboardAPI,
} from "../services/api";

const AdminDataContext = createContext(null);

const loadFromStorage = (key, fallback) => {
  try {
    const saved = localStorage.getItem(`selvas_${key}`);
    if (saved) return JSON.parse(saved);
  } catch (e) {
    console.error(`Error loading ${key} from storage:`, e);
  }
  return fallback;
};

export const AdminDataProvider = ({ children }) => {
  const [dailyCA, setDailyCA] = useState(() =>
    loadFromStorage("daily_ca", initialDailyCA)
  );
  const [monthlyCA, setMonthlyCA] = useState(() =>
    loadFromStorage("monthly_ca", initialMonthlyCA)
  );
  const [prelimsTests, setPrelimsTests] = useState(() =>
    loadFromStorage("prelims_tests", initialPrelimsTests)
  );
  const [mainsInitiatives, setMainsInitiatives] = useState(() =>
    loadFromStorage("mains_initiatives", initialMainsInitiatives)
  );
  const [studentResults, setStudentResults] = useState(() => {
    const raw = loadFromStorage("student_results", initialStudentResults);
    return (raw || []).map((r, i) => ({
      ...r,
      studentName: r?.studentName || "UPSC Aspirant",
      regId: r?.regId || `SIA-2026-${1000 + i}`,
      initiativeName: r?.initiativeName || "UPSC Initiative",
      examType: r?.examType || "Prelims",
      resultStatus: r?.resultStatus || r?.status || "Evaluated",
      correctCount: r?.correctCount ?? r?.correctAnswers ?? 4,
      incorrectCount: r?.incorrectCount ?? r?.incorrectAnswers ?? 1,
      unansweredCount: r?.unansweredCount ?? r?.unanswered ?? 0,
    }));
  });
  const [activities, setActivities] = useState(() =>
    loadFromStorage("activities", initialActivities)
  );
  const [settings, setSettings] = useState(() =>
    loadFromStorage("settings", initialSettings)
  );
  const [prelimsQuestionBank, setPrelimsQuestionBank] = useState(() =>
    loadFromStorage("prelims_qb", initialPrelimsQuestionBank)
  );
  const [mainsQuestionBank, setMainsQuestionBank] = useState(() =>
    loadFromStorage("mains_qb", initialMainsQuestionBank)
  );
  const [dbFolders, setDbFolders] = useState([]);
  const [isSyncing, setIsSyncing] = useState(false);

  // Sync state to localStorage for offline cache
  useEffect(() => {
    localStorage.setItem("selvas_daily_ca", JSON.stringify(dailyCA));
  }, [dailyCA]);

  useEffect(() => {
    localStorage.setItem("selvas_monthly_ca", JSON.stringify(monthlyCA));
  }, [monthlyCA]);

  useEffect(() => {
    localStorage.setItem("selvas_prelims_tests", JSON.stringify(prelimsTests));
  }, [prelimsTests]);

  useEffect(() => {
    localStorage.setItem(
      "selvas_mains_initiatives",
      JSON.stringify(mainsInitiatives)
    );
  }, [mainsInitiatives]);

  useEffect(() => {
    localStorage.setItem(
      "selvas_student_results",
      JSON.stringify(studentResults)
    );
  }, [studentResults]);

  useEffect(() => {
    localStorage.setItem("selvas_prelims_qb", JSON.stringify(prelimsQuestionBank));
  }, [prelimsQuestionBank]);

  useEffect(() => {
    localStorage.setItem("selvas_mains_qb", JSON.stringify(mainsQuestionBank));
  }, [mainsQuestionBank]);

  useEffect(() => {
    localStorage.setItem("selvas_activities", JSON.stringify(activities));
  }, [activities]);

  useEffect(() => {
    localStorage.setItem("selvas_settings", JSON.stringify(settings));
  }, [settings]);

  // Log an activity helper
  const addActivity = (action, target, type = "admin") => {
    const newAct = {
      id: `act-${Date.now()}`,
      action,
      target,
      user: "Admin",
      time: "Just now",
      timestamp: new Date().toISOString(),
      type,
    };
    setActivities((prev) => [newAct, ...prev]);
  };

  // Synchronize state from Backend MongoDB APIs
  const refreshAllData = useCallback(async () => {
    setIsSyncing(true);
    try {
      const [
        dailyRes,
        monthlyRes,
        prelimsRes,
        mainsRes,
        prelimsQbRes,
        mainsQbRes,
        foldersRes,
        pResultsRes,
        mResultsRes,
        recentRes,
      ] = await Promise.allSettled([
        dailyCAAPI.getAll(),
        monthlyCAAPI.getAll(),
        prelimsAPI.getAll(),
        mainsAPI.getAll(),
        qbAPI.getQuestions({ examType: "prelims" }),
        qbAPI.getQuestions({ examType: "mains" }),
        qbAPI.getFolders(),
        resultsAPI.getPrelims(),
        resultsAPI.getMains(),
        dashboardAPI.getRecent(),
      ]);

      if (dailyRes.status === "fulfilled" && dailyRes.value?.data) {
        setDailyCA(
          dailyRes.value.data.map((item) => ({
            ...item,
            id: item._id,
            status: item.status === "published" ? "Published" : "Draft",
            date: item.publicationDate
              ? new Date(item.publicationDate).toISOString().split("T")[0]
              : "",
          }))
        );
      }

      if (monthlyRes.status === "fulfilled" && monthlyRes.value?.data) {
        setMonthlyCA(
          monthlyRes.value.data.map((item) => ({
            ...item,
            id: item._id,
            status: item.status === "published" ? "Published" : "Draft",
          }))
        );
      }

      if (prelimsRes.status === "fulfilled" && prelimsRes.value?.data) {
        setPrelimsTests(
          prelimsRes.value.data.map((item) => ({
            ...item,
            id: item._id,
            status: item.status === "published" ? "Published" : "Draft",
            questionsCount: item.questions?.length || item.totalQuestions || 0,
          }))
        );
      }

      if (mainsRes.status === "fulfilled" && mainsRes.value?.data) {
        setMainsInitiatives(
          mainsRes.value.data.map((item) => ({
            ...item,
            id: item._id,
            status: item.status === "published" ? "Published" : "Draft",
          }))
        );
      }

      if (prelimsQbRes.status === "fulfilled" && prelimsQbRes.value?.data) {
        setPrelimsQuestionBank(
          prelimsQbRes.value.data.map((item) => ({
            ...item,
            id: item._id,
            status: "Active",
            dateAdded: item.createdAt ? item.createdAt.split("T")[0] : "",
          }))
        );
      }

      if (mainsQbRes.status === "fulfilled" && mainsQbRes.value?.data) {
        setMainsQuestionBank(
          mainsQbRes.value.data.map((item) => ({
            ...item,
            id: item._id,
            status: "Active",
            dateAdded: item.createdAt ? item.createdAt.split("T")[0] : "",
          }))
        );
      }

      if (foldersRes.status === "fulfilled" && foldersRes.value?.data) {
        setDbFolders(foldersRes.value.data);
      }

      // Combine Prelims & Mains Results
      const combinedResults = [];
      if (pResultsRes.status === "fulfilled" && pResultsRes.value?.data) {
        pResultsRes.value.data.forEach((p) => {
          combinedResults.push({
            id: p._id,
            _id: p._id,
            studentName: p.studentName,
            email: p.studentEmail,
            studentEmail: p.studentEmail,
            phone: p.phone || p.mobileNumber || "+91 98401 22334",
            mobileNumber: p.mobileNumber || p.phone || "+91 98401 22334",
            location: p.location || "Chennai, Tamil Nadu",
            avatar: p.avatar || "",
            regId: p.regId,
            studentId: p.studentId,
            testId: p.testId,
            examType: "Prelims",
            testName: p.testTitle,
            initiativeName: p.testTitle,
            totalQuestions: p.totalQuestions,
            correctCount: p.correctCount,
            incorrectCount: p.incorrectCount,
            unansweredCount: p.unansweredCount,
            marksObtained: p.marksObtained,
            totalMarks: p.totalMarks,
            percentage: p.percentage,
            submissionDate: p.submittedAt ? new Date(p.submittedAt).toISOString().split("T")[0] : "",
            resultStatus: "Evaluated",
            answers: p.answers,
          });
        });
      }

      if (mResultsRes.status === "fulfilled" && mResultsRes.value?.data) {
        mResultsRes.value.data.forEach((m) => {
          combinedResults.push({
            id: m._id,
            _id: m._id,
            studentName: m.studentName,
            email: m.studentEmail,
            studentEmail: m.studentEmail,
            phone: m.phone || m.mobileNumber || "+91 98401 22334",
            mobileNumber: m.mobileNumber || m.phone || "+91 98401 22334",
            location: m.location || "Chennai, Tamil Nadu",
            avatar: m.avatar || "",
            regId: m.regId,
            studentId: m.studentId,
            initiativeId: m.initiativeId,
            examType: "Mains",
            initiativeName: m.initiativeTitle,
            gsPaper: m.gsPaper,
            questionText: m.questionText,
            submittedAnswer: m.submittedAnswer,
            submittedPdfUrl: m.submittedPdfUrl,
            wordCount: m.wordCount,
            evaluationStatus: m.evaluationStatus === "evaluated" ? "Evaluated" : "Pending",
            resultStatus: m.evaluationStatus === "evaluated" ? "Evaluated" : "Pending",
            marksObtained: m.marksAwarded ?? 0,
            marksAwarded: m.marksAwarded,
            totalMarks: m.maxMarks || 10,
            maxMarks: m.maxMarks || 10,
            feedback: m.evaluatorFeedback || "",
            evaluatorFeedback: m.evaluatorFeedback || "",
            evaluatorName: m.evaluatorName,
            isResultPublished: m.isResultPublished,
            submissionDate: m.submittedAt ? new Date(m.submittedAt).toISOString().split("T")[0] : "",
          });
        });
      }

      if (combinedResults.length > 0) {
        setStudentResults(combinedResults);
      }

      if (recentRes.status === "fulfilled" && recentRes.value?.recentActivities) {
        setActivities(
          recentRes.value.recentActivities.map((act) => ({
            id: act._id,
            action: act.action,
            target: act.description,
            user: act.user?.name || "Admin",
            time: "Recently",
            timestamp: act.timestamp,
            type: act.module?.toLowerCase() || "admin",
          }))
        );
      }
    } catch (e) {
      console.warn("Error refreshing data from backend:", e.message);
    } finally {
      setIsSyncing(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    refreshAllData();
  }, [refreshAllData]);

  // --- Daily Current Affairs CRUD ---
  const addDailyCA = async (item, file) => {
    try {
      const formData = new FormData();
      formData.append("title", item.title || "");
      formData.append("description", item.description || item.shortDescription || item.fullContent || "");
      formData.append("publicationDate", item.date || item.publicationDate || new Date().toISOString());
      formData.append("category", item.category || "Polity");
      formData.append("status", (item.status || "Draft").toLowerCase());
      if (file) {
        formData.append("pdfFile", file);
      }
      const res = await dailyCAAPI.create(formData);
      if (res.success && res.data) {
        const created = {
          ...res.data,
          id: res.data._id,
          status: res.data.status === "published" ? "Published" : "Draft",
          date: res.data.publicationDate ? res.data.publicationDate.split("T")[0] : "",
        };
        setDailyCA((prev) => [created, ...prev]);
        addActivity("Uploaded Daily Current Affairs", created.title, "create");
        return created;
      }
    } catch (err) {
      console.warn("Backend create Daily CA failed, saving locally:", err.message);
    }

    const newItem = {
      ...item,
      id: `dca-${Date.now()}`,
      views: item.views || 0,
      status: item.status || "Draft",
    };
    setDailyCA((prev) => [newItem, ...prev]);
    addActivity("Uploaded Daily Current Affairs", newItem.title, "create");
    return newItem;
  };

  const updateDailyCA = async (id, updates, file) => {
    try {
      const formData = new FormData();
      if (updates.title) formData.append("title", updates.title);
      if (updates.description || updates.shortDescription || updates.fullContent)
        formData.append("description", updates.description || updates.shortDescription || updates.fullContent);
      if (updates.category) formData.append("category", updates.category);
      if (updates.date) formData.append("publicationDate", updates.date);
      if (updates.status) formData.append("status", updates.status.toLowerCase());
      if (file) formData.append("pdfFile", file);

      await dailyCAAPI.update(id, formData);
    } catch (e) {
      console.warn("Backend update Daily CA failed:", e.message);
    }

    setDailyCA((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
    addActivity("Updated Daily Current Affairs", updates.title || `Item #${id}`, "update");
  };

  const deleteDailyCA = async (id) => {
    try {
      await dailyCAAPI.delete(id);
    } catch (e) {
      console.warn("Backend delete Daily CA failed:", e.message);
    }
    const item = dailyCA.find((i) => i.id === id);
    setDailyCA((prev) => prev.filter((i) => i.id !== id));
    addActivity("Deleted Daily Current Affairs", item?.title || `Item #${id}`, "delete");
  };

  const togglePublishDailyCA = async (id) => {
    const item = dailyCA.find((i) => i.id === id);
    const nextStatus = item?.status === "Published" ? "Draft" : "Published";

    try {
      await dailyCAAPI.toggleStatus(id, nextStatus.toLowerCase());
    } catch (e) {
      console.warn("Backend toggle Daily CA failed:", e.message);
    }

    setDailyCA((prev) =>
      prev.map((it) => (it.id === id ? { ...it, status: nextStatus } : it))
    );
    addActivity(
      `${nextStatus === "Published" ? "Published" : "Unpublished"} Daily Current Affairs`,
      item?.title || "",
      "status"
    );
  };

  // --- Monthly Current Affairs CRUD ---
  const addMonthlyCA = async (item, pdfFile, thumbFile) => {
    try {
      const formData = new FormData();
      formData.append("month", item.month || "January");
      formData.append("year", item.year || new Date().getFullYear());
      formData.append("title", item.title || "");
      formData.append("description", item.description || "");
      formData.append("status", (item.status || "Published").toLowerCase());
      if (pdfFile) formData.append("pdfFile", pdfFile);
      if (thumbFile) formData.append("thumbnail", thumbFile);

      const res = await monthlyCAAPI.create(formData);
      if (res.success && res.data) {
        const created = {
          ...res.data,
          id: res.data._id,
          status: res.data.status === "published" ? "Published" : "Draft",
        };
        setMonthlyCA((prev) => [created, ...prev]);
        addActivity("Uploaded Monthly Current Affairs", created.title, "create");
        return created;
      }
    } catch (e) {
      console.warn("Backend create Monthly CA failed:", e.message);
    }

    const newItem = {
      ...item,
      id: `mca-${Date.now()}`,
      downloads: item.downloads || 0,
      pages: item.pages || 48,
      status: item.status || "Published",
    };
    setMonthlyCA((prev) => [newItem, ...prev]);
    addActivity("Uploaded Monthly Current Affairs", newItem.title, "create");
    return newItem;
  };

  const updateMonthlyCA = async (id, updates, pdfFile, thumbFile) => {
    try {
      const formData = new FormData();
      if (updates.month) formData.append("month", updates.month);
      if (updates.year) formData.append("year", updates.year);
      if (updates.title) formData.append("title", updates.title);
      if (updates.description) formData.append("description", updates.description);
      if (updates.status) formData.append("status", updates.status.toLowerCase());
      if (pdfFile) formData.append("pdfFile", pdfFile);
      if (thumbFile) formData.append("thumbnail", thumbFile);

      await monthlyCAAPI.update(id, formData);
    } catch (e) {
      console.warn("Backend update Monthly CA failed:", e.message);
    }

    setMonthlyCA((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
    addActivity("Updated Monthly Current Affairs", updates.title || `Item #${id}`, "update");
  };

  const deleteMonthlyCA = async (id) => {
    try {
      await monthlyCAAPI.delete(id);
    } catch (e) {
      console.warn("Backend delete Monthly CA failed:", e.message);
    }
    const item = monthlyCA.find((i) => i.id === id);
    setMonthlyCA((prev) => prev.filter((i) => i.id !== id));
    addActivity("Deleted Monthly Current Affairs", item?.title || `Item #${id}`, "delete");
  };

  const togglePublishMonthlyCA = async (id) => {
    const item = monthlyCA.find((i) => i.id === id);
    const nextStatus = item?.status === "Published" ? "Draft" : "Published";

    try {
      await monthlyCAAPI.toggleStatus(id, nextStatus.toLowerCase());
    } catch (e) {
      console.warn("Backend toggle Monthly CA failed:", e.message);
    }

    setMonthlyCA((prev) =>
      prev.map((it) => (it.id === id ? { ...it, status: nextStatus } : it))
    );
    addActivity(
      `${nextStatus === "Published" ? "Published" : "Unpublished"} Monthly Magazine`,
      item?.title || "",
      "status"
    );
  };

  // --- Prelims Tests CRUD ---
  const addPrelimsTest = async (test) => {
    try {
      const res = await prelimsAPI.create({
        title: test.title,
        description: test.description,
        date: test.date,
        folder: test.folder || "Polity",
        category: test.category || "GS Paper I",
        durationMinutes: test.duration || test.durationMinutes || 120,
        marksPerQuestion: test.marksPerQuestion || 2.0,
        negativeMarks: test.negativeMarks !== undefined ? test.negativeMarks : (test.negativeMarking !== undefined ? test.negativeMarking : 0.66),
        status: (test.status || "Published").toLowerCase(),
        selectedQuestionIds: test.selectedQuestionIds || [],
        manualQuestions: test.questions || [],
      });
      if (res.success && res.data) {
        const created = {
          ...res.data,
          id: res.data._id,
          date: test.date || res.data.date || new Date().toISOString().split("T")[0],
          folder: test.folder || res.data.folder || "Polity",
          status: res.data.status === "published" ? "Published" : "Draft",
          questionsCount: res.data.questions?.length || 0,
        };
        setPrelimsTests((prev) => [created, ...prev]);
        addActivity("Created Prelims Test", created.title, "create");
        return created;
      }
    } catch (e) {
      console.warn("Backend create Prelims Test failed:", e.message);
    }

    const newTest = {
      ...test,
      id: `pt-${Date.now()}`,
      date: test.date || new Date().toISOString().split("T")[0],
      folder: test.folder || "Polity",
      participantsCount: test.participantsCount || 0,
      avgScore: test.avgScore || 0,
      status: test.status || "Published",
      questionsCount: test.questions ? test.questions.length : (test.questionsCount || 5),
    };
    setPrelimsTests((prev) => [newTest, ...prev]);
    addActivity("Created Prelims Test", newTest.title, "create");
    return newTest;
  };

  const updatePrelimsTest = async (id, updates) => {
    try {
      await prelimsAPI.update(id, {
        ...updates,
        date: updates.date,
        folder: updates.folder,
        negativeMarks: updates.negativeMarks !== undefined ? updates.negativeMarks : (updates.negativeMarking !== undefined ? updates.negativeMarking : undefined),
        status: updates.status ? updates.status.toLowerCase() : undefined,
      });
    } catch (e) {
      console.warn("Backend update Prelims Test failed:", e.message);
    }

    setPrelimsTests((prev) =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = { ...item, ...updates };
          if (updates.questions) {
            updated.questionsCount = updates.questions.length;
          }
          return updated;
        }
        return item;
      })
    );
    addActivity("Updated Prelims Test", updates.title || `Test #${id}`, "update");
  };

  const deletePrelimsTest = async (id) => {
    try {
      await prelimsAPI.delete(id);
    } catch (e) {
      console.warn("Backend delete Prelims Test failed:", e.message);
    }
    const test = prelimsTests.find((t) => t.id === id);
    setPrelimsTests((prev) => prev.filter((t) => t.id !== id));
    addActivity("Deleted Prelims Test", test?.title || `Test #${id}`, "delete");
  };

  const togglePublishPrelimsTest = async (id) => {
    const item = prelimsTests.find((t) => t.id === id);
    const nextStatus = item?.status === "Published" ? "Draft" : "Published";

    try {
      await prelimsAPI.toggleStatus(id, nextStatus.toLowerCase());
    } catch (e) {
      console.warn("Backend toggle Prelims Test failed:", e.message);
    }

    setPrelimsTests((prev) =>
      prev.map((it) => (it.id === id ? { ...it, status: nextStatus } : it))
    );
    addActivity(
      `${nextStatus === "Published" ? "Published" : "Unpublished"} Prelims Test`,
      item?.title || "",
      "status"
    );
  };

  // --- Mains Initiatives CRUD ---
  const addMainsInitiative = async (item, refPdfFile) => {
    try {
      const formData = new FormData();
      formData.append("title", item.title || "");
      formData.append("gsPaper", item.gsPaper || "GS Paper I");
      formData.append("subject", item.subject || item.folder || "General");
      formData.append("folder", item.folder || item.subject || "Indian Polity");
      formData.append("description", item.description || item.instructions || "");
      const testDate = item.date || item.deadline || new Date().toISOString().split("T")[0];
      formData.append("date", testDate);
      formData.append("deadline", item.deadline || testDate);
      formData.append("status", (item.status || "Published").toLowerCase());
      if (item.topic) formData.append("topic", item.topic);
      if (item.question) formData.append("question", item.question);
      if (item.maxMarks) formData.append("maxMarks", item.maxMarks);
      if (item.wordLimit) formData.append("wordLimit", item.wordLimit);
      if (item.instructions) formData.append("instructions", item.instructions);
      if (item.questions) formData.append("manualQuestions", JSON.stringify(item.questions));
      if (refPdfFile) formData.append("referencePdf", refPdfFile);

      const res = await mainsAPI.create(formData);
      if (res.success && res.data) {
        const created = {
          ...res.data,
          id: res.data._id,
          folder: item.folder || res.data.folder || item.subject || "Indian Polity",
          date: testDate,
          status: res.data.status === "published" ? "Published" : "Draft",
        };
        setMainsInitiatives((prev) => [created, ...prev]);
        addActivity("Created Mains Initiative", created.title, "create");
        return created;
      }
    } catch (e) {
      console.warn("Backend create Mains Initiative failed:", e.message);
    }

    const newItem = {
      ...item,
      id: `mains-${Date.now()}`,
      folder: item.folder || item.subject || "Indian Polity",
      date: item.date || item.deadline || new Date().toISOString().split("T")[0],
      submissionsCount: item.submissionsCount || 0,
      status: item.status || "Published",
    };
    setMainsInitiatives((prev) => [newItem, ...prev]);
    addActivity("Created Mains Initiative", newItem.title, "create");
    return newItem;
  };

  const updateMainsInitiative = async (id, updates, refPdfFile) => {
    try {
      const formData = new FormData();
      if (updates.title) formData.append("title", updates.title);
      if (updates.gsPaper) formData.append("gsPaper", updates.gsPaper);
      if (updates.subject) formData.append("subject", updates.subject);
      if (updates.folder) formData.append("folder", updates.folder);
      if (updates.description) formData.append("description", updates.description);
      if (updates.date) formData.append("date", updates.date);
      if (updates.deadline) formData.append("deadline", updates.deadline);
      if (updates.status) formData.append("status", updates.status.toLowerCase());
      if (updates.topic) formData.append("topic", updates.topic);
      if (updates.question) formData.append("question", updates.question);
      if (updates.maxMarks) formData.append("maxMarks", updates.maxMarks);
      if (updates.wordLimit) formData.append("wordLimit", updates.wordLimit);
      if (updates.instructions) formData.append("instructions", updates.instructions);
      if (updates.questions) formData.append("manualQuestions", JSON.stringify(updates.questions));
      if (refPdfFile) formData.append("referencePdf", refPdfFile);

      await mainsAPI.update(id, formData);
    } catch (e) {
      console.warn("Backend update Mains Initiative failed:", e.message);
    }

    setMainsInitiatives((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
    addActivity("Updated Mains Initiative", updates.title || `Initiative #${id}`, "update");
  };

  const deleteMainsInitiative = async (id) => {
    try {
      await mainsAPI.delete(id);
    } catch (e) {
      console.warn("Backend delete Mains Initiative failed:", e.message);
    }
    const item = mainsInitiatives.find((i) => i.id === id);
    setMainsInitiatives((prev) => prev.filter((i) => i.id !== id));
    addActivity("Deleted Mains Initiative", item?.title || `Initiative #${id}`, "delete");
  };

  const togglePublishMainsInitiative = async (id) => {
    const item = mainsInitiatives.find((i) => i.id === id);
    const nextStatus = item?.status === "Published" ? "Draft" : "Published";

    try {
      await mainsAPI.toggleStatus(id, nextStatus.toLowerCase());
    } catch (e) {
      console.warn("Backend toggle Mains Initiative failed:", e.message);
    }

    setMainsInitiatives((prev) =>
      prev.map((it) => (it.id === id ? { ...it, status: nextStatus } : it))
    );
    addActivity(
      `${nextStatus === "Published" ? "Published" : "Unpublished"} Mains Initiative`,
      item?.title || "",
      "status"
    );
  };

  // Evaluate a Mains submission
  const evaluateMainsSubmission = async (
    resultId,
    { marksObtained, feedback, evaluationStatus = "Evaluated" }
  ) => {
    try {
      await mainsAPI.evaluateSubmission(resultId, {
        marksAwarded: marksObtained,
        evaluatorFeedback: feedback,
        publishResult: true,
      });
    } catch (e) {
      console.warn("Backend evaluate submission failed:", e.message);
    }

    setStudentResults((prev) =>
      prev.map((r) => {
        if (r.id === resultId || r._id === resultId) {
          const marksNum = Number(marksObtained);
          const pct = Math.round((marksNum / (r.totalMarks || 15)) * 100);
          return {
            ...r,
            marksObtained: marksNum,
            marksAwarded: marksNum,
            percentage: pct,
            feedback,
            evaluatorFeedback: feedback,
            evaluationStatus,
            resultStatus: "Evaluated",
            isResultPublished: true,
          };
        }
        return r;
      })
    );

    const targetStudent = studentResults.find((r) => r.id === resultId || r._id === resultId);
    addActivity(
      `Evaluated Mains Answer for ${targetStudent?.studentName || "Student"}`,
      `Marks: ${marksObtained} / ${targetStudent?.totalMarks || 15}`,
      "evaluate"
    );
  };

  // --- Student Results CRUD ---
  const addStudentResult = (res) => {
    const newRes = {
      ...res,
      id: `res-${Date.now()}`,
    };
    setStudentResults((prev) => [newRes, ...prev]);
    return newRes;
  };

  const updateStudentResult = (id, updates) => {
    setStudentResults((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...updates } : r))
    );
    addActivity("Updated Student Result", `#${id}`, "update");
  };

  const deleteStudentResult = (id) => {
    setStudentResults((prev) => prev.filter((r) => r.id !== id));
    addActivity("Removed Student Result Record", `#${id}`, "delete");
  };

  // --- Settings ---
  const updateSettingsData = (newSettings) => {
    setSettings((prev) => ({ ...prev, ...newSettings }));
    addActivity("Updated Academy & Dashboard Settings", "System Preferences", "settings");
  };

  // --- Prelims Question Bank ---
  const addPrelimsQuestion = async (questionData) => {
    try {
      const res = await qbAPI.createQuestion({
        ...questionData,
        examType: "prelims",
      });
      if (res.success && res.data) {
        const newQ = {
          ...res.data,
          id: res.data._id,
          status: "Active",
          dateAdded: res.data.createdAt ? res.data.createdAt.split("T")[0] : "",
        };
        setPrelimsQuestionBank((prev) => [newQ, ...prev]);
        addActivity("Added Prelims Question", newQ.questionText?.substring(0, 40) + "...", "create");
        return newQ;
      }
    } catch (e) {
      console.warn("Backend add Prelims question failed:", e.message);
    }

    const newQ = {
      ...questionData,
      id: questionData.id || `pq-${Date.now()}`,
      dateAdded: questionData.dateAdded || new Date().toISOString().split("T")[0],
      status: questionData.status || "Active",
    };
    setPrelimsQuestionBank((prev) => [newQ, ...prev]);
    addActivity("Added Prelims Question", newQ.questionText ? newQ.questionText.substring(0, 40) + "..." : "New Question", "create");
    return newQ;
  };

  const updatePrelimsQuestion = async (id, updatedData) => {
    try {
      await qbAPI.updateQuestion(id, updatedData);
    } catch (e) {
      console.warn("Backend update Prelims question failed:", e.message);
    }
    setPrelimsQuestionBank((prev) =>
      prev.map((q) => (q.id === id ? { ...q, ...updatedData } : q))
    );
    addActivity("Updated Prelims Question", `ID: ${id}`, "edit");
  };

  const deletePrelimsQuestion = async (id) => {
    try {
      await qbAPI.deleteQuestion(id);
    } catch (e) {
      console.warn("Backend delete Prelims question failed:", e.message);
    }
    setPrelimsQuestionBank((prev) => prev.filter((q) => q.id !== id));
    addActivity("Deleted Prelims Question", `ID: ${id}`, "delete");
  };

  const movePrelimsQuestions = async (ids, targetSubject, targetFolderId) => {
    if (targetFolderId) {
      try {
        await qbAPI.moveQuestions(ids, targetFolderId);
      } catch (e) {
        console.warn("Backend move questions failed:", e.message);
      }
    }
    setPrelimsQuestionBank((prev) =>
      prev.map((q) => (ids.includes(q.id) ? { ...q, subject: targetSubject, folderId: targetFolderId } : q))
    );
    addActivity("Moved Prelims Questions", `Moved ${ids.length} question(s) to ${targetSubject}`, "edit");
  };

  const bulkDeletePrelimsQuestions = async (ids) => {
    await Promise.allSettled(ids.map((id) => qbAPI.deleteQuestion(id)));
    setPrelimsQuestionBank((prev) => prev.filter((q) => !ids.includes(q.id)));
    addActivity("Batch Deleted Prelims Questions", `Removed ${ids.length} question(s)`, "delete");
  };

  const bulkAddPrelimsQuestions = (questionsArray) => {
    const timestamp = Date.now();
    const formatted = questionsArray.map((q, idx) => ({
      ...q,
      id: q.id || `pq-${timestamp}-${idx}`,
      dateAdded: q.dateAdded || new Date().toISOString().split("T")[0],
      status: q.status || "Active",
    }));
    setPrelimsQuestionBank((prev) => [...formatted, ...prev]);
    addActivity("Bulk Imported Prelims Questions", `Imported ${formatted.length} questions`, "create");
    return formatted;
  };

  // --- Mains Question Bank ---
  const addMainsQuestion = async (questionData) => {
    try {
      const res = await qbAPI.createQuestion({
        ...questionData,
        examType: "mains",
      });
      if (res.success && res.data) {
        const newQ = {
          ...res.data,
          id: res.data._id,
          status: "Active",
          dateAdded: res.data.createdAt ? res.data.createdAt.split("T")[0] : "",
        };
        setMainsQuestionBank((prev) => [newQ, ...prev]);
        addActivity("Added Mains Question", newQ.questionText?.substring(0, 40) + "...", "create");
        return newQ;
      }
    } catch (e) {
      console.warn("Backend add Mains question failed:", e.message);
    }

    const newQ = {
      ...questionData,
      id: questionData.id || `mq-${Date.now()}`,
      dateAdded: questionData.dateAdded || new Date().toISOString().split("T")[0],
      status: questionData.status || "Active",
    };
    setMainsQuestionBank((prev) => [newQ, ...prev]);
    addActivity("Added Mains Question", newQ.questionText ? newQ.questionText.substring(0, 40) + "..." : "New Question", "create");
    return newQ;
  };

  const updateMainsQuestion = async (id, updatedData) => {
    try {
      await qbAPI.updateQuestion(id, updatedData);
    } catch (e) {
      console.warn("Backend update Mains question failed:", e.message);
    }
    setMainsQuestionBank((prev) =>
      prev.map((q) => (q.id === id ? { ...q, ...updatedData } : q))
    );
    addActivity("Updated Mains Question", `ID: ${id}`, "edit");
  };

  const deleteMainsQuestion = async (id) => {
    try {
      await qbAPI.deleteQuestion(id);
    } catch (e) {
      console.warn("Backend delete Mains question failed:", e.message);
    }
    setMainsQuestionBank((prev) => prev.filter((q) => q.id !== id));
    addActivity("Deleted Mains Question", `ID: ${id}`, "delete");
  };

  const moveMainsQuestions = async (ids, targetSubject, targetGsPaper, targetFolderId) => {
    if (targetFolderId) {
      try {
        await qbAPI.moveQuestions(ids, targetFolderId);
      } catch (e) {
        console.warn("Backend move Mains questions failed:", e.message);
      }
    }
    setMainsQuestionBank((prev) =>
      prev.map((q) => {
        if (ids.includes(q.id)) {
          return {
            ...q,
            subject: targetSubject,
            ...(targetGsPaper ? { gsPaper: targetGsPaper } : {}),
            ...(targetFolderId ? { folderId: targetFolderId } : {}),
          };
        }
        return q;
      })
    );
    addActivity("Moved Mains Questions", `Moved ${ids.length} question(s) to ${targetSubject}`, "edit");
  };

  const bulkDeleteMainsQuestions = async (ids) => {
    await Promise.allSettled(ids.map((id) => qbAPI.deleteQuestion(id)));
    setMainsQuestionBank((prev) => prev.filter((q) => !ids.includes(q.id)));
    addActivity("Batch Deleted Mains Questions", `Removed ${ids.length} question(s)`, "delete");
  };

  const bulkAddMainsQuestions = (questionsArray) => {
    const timestamp = Date.now();
    const formatted = questionsArray.map((q, idx) => ({
      ...q,
      id: q.id || `mq-${timestamp}-${idx}`,
      dateAdded: q.dateAdded || new Date().toISOString().split("T")[0],
      status: q.status || "Active",
    }));
    setMainsQuestionBank((prev) => [...formatted, ...prev]);
    addActivity("Bulk Imported Mains Questions", `Imported ${formatted.length} questions`, "create");
    return formatted;
  };

  // Reset to default initial data
  const resetToDefaults = () => {
    setDailyCA(initialDailyCA);
    setMonthlyCA(initialMonthlyCA);
    setPrelimsTests(initialPrelimsTests);
    setMainsInitiatives(initialMainsInitiatives);
    setStudentResults(initialStudentResults);
    setPrelimsQuestionBank(initialPrelimsQuestionBank);
    setMainsQuestionBank(initialMainsQuestionBank);
    setActivities(initialActivities);
    setSettings(initialSettings);
    addActivity("Restored System Demo Defaults", "All collections refreshed", "reset");
  };

  return (
    <AdminDataContext.Provider
      value={{
        dailyCA,
        addDailyCA,
        updateDailyCA,
        deleteDailyCA,
        togglePublishDailyCA,

        monthlyCA,
        addMonthlyCA,
        updateMonthlyCA,
        deleteMonthlyCA,
        togglePublishMonthlyCA,

        prelimsTests,
        addPrelimsTest,
        updatePrelimsTest,
        deletePrelimsTest,
        togglePublishPrelimsTest,

        mainsInitiatives,
        addMainsInitiative,
        updateMainsInitiative,
        deleteMainsInitiative,
        togglePublishMainsInitiative,
        evaluateMainsSubmission,

        prelimsQuestionBank,
        addPrelimsQuestion,
        updatePrelimsQuestion,
        deletePrelimsQuestion,
        movePrelimsQuestions,
        bulkDeletePrelimsQuestions,
        bulkAddPrelimsQuestions,

        mainsQuestionBank,
        addMainsQuestion,
        updateMainsQuestion,
        deleteMainsQuestion,
        moveMainsQuestions,
        bulkDeleteMainsQuestions,
        bulkAddMainsQuestions,

        dbFolders,
        studentResults,
        addStudentResult,
        updateStudentResult,
        deleteStudentResult,

        activities,
        addActivity,

        settings,
        updateSettings: updateSettingsData,
        resetToDefaults,

        isSyncing,
        refreshAllData,
      }}
    >
      {children}
    </AdminDataContext.Provider>
  );
};

export const useAdminData = () => {
  const context = useContext(AdminDataContext);
  if (!context) {
    throw new Error("useAdminData must be used within an AdminDataProvider");
  }
  return context;
};
